import React, { useMemo, useCallback, MouseEventHandler, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { pluralizeToString } from 'lib/string/pluralize'
import { getRefundStatus, Refundable, generateRefundablePolicy, getRefundDescriptions } from '../utils/getCancellationPolicyStatus'
import ModalContext from 'contexts/ModalContext'
import Divider from 'LuxKit/Divider'

const TermsGrid = styled.div`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  cancellationPolicies: Array<App.RoomRateCancellationPolicy>
  offerType: string
}

function StaticConnectionCancellationPoliciesComponent(props: Props) {
  const { cancellationPolicies, offerType } = props

  const refundable = useMemo<Refundable | null>(() => getRefundStatus(cancellationPolicies), [cancellationPolicies])
  const refundDescriptions = useMemo(() => getRefundDescriptions(offerType), [offerType])
  const nonRefundablePolicy = useMemo(() => cancellationPolicies.find(policy => (policy.policy.unit === 'percentage' && policy.policy.value === 100) || policy.nonRefundable), [cancellationPolicies])
  const simplifiedPolicies = useMemo(() => cancellationPolicies.map(policy => ({
    policy: {
      leadDaysMin: policy.policy.leadDaysMin,
      value: policy.policy.value,
      unit: policy.policy.unit,
    },
    nonRefundable: policy.nonRefundable,
  })), [cancellationPolicies])

  const refundablePolicy = useMemo(() => generateRefundablePolicy(cancellationPolicies), [cancellationPolicies])
  // Order policies by leadDaysMin, remove non-refundable policy if it exists, and add refundable policy if it exists
  const orderedPolicies = useMemo(() => {
    const policies = [...simplifiedPolicies, ...(refundablePolicy ? [refundablePolicy] : [])]
    return policies
      .sort((a, b) => b.policy.leadDaysMin - a.policy.leadDaysMin)
      .filter(policy => policy.policy.value !== 100 && !policy.nonRefundable)
  }, [simplifiedPolicies, refundablePolicy])

  const showModal = useContext(ModalContext)

  const openPolicyModal = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.preventDefault()
    showModal(<Modal
      title={refundable !== null ? refundDescriptions[refundable] : 'Cancellation policy'}
    >
      <Group direction="vertical" gap={40}>
        {refundable !== Refundable.Non && <BodyText variant="medium">
          Things don’t always work out, so enjoy peace of mind knowing that the following cancellation options are available to you.{' '}
        </BodyText>}
        <Group direction="vertical" gap={16}>
          <Divider kind="primary" />
          {orderedPolicies.length > 0 && orderedPolicies.map(policy =>
            <>
              <TermsGrid>
                <BodyText variant="medium" weight="bold">
                  Up to {policy.policy.leadDaysMin} days prior to check-in
                </BodyText>
                <Group direction="vertical" gap={16}>

                  {policy.policy.unit === 'percentage' && policy.policy.value === 0 && (
                    <>
                      <BodyText variant="medium">
                        Free cancellation
                      </BodyText>
                      <BodyText variant="medium">
                        Accommodation bookings can be cancelled with a full refund provided the cancellation is made no less than {pluralizeToString('day', policy.policy.leadDaysMin)} prior to check-in.
                      </BodyText>
                    </>
                  )}
                  {policy.policy.unit === 'percentage' && policy.policy.value !== 0 && (
                    <>
                      <BodyText variant="medium">
                        {100 - policy.policy.value}% refund of accommodation purchase price to credit
                      </BodyText>
                      <BodyText variant="medium">
                        Accommodation bookings can be cancelled with a partial refund provided the cancellation is made no less than {pluralizeToString('day', policy.policy.leadDaysMin)} prior to check-in.
                      </BodyText>
                    </>
                  )}
                  {policy.policy.unit === 'amount' && (
                    <>
                      <BodyText variant="medium">
                        {policy.policy.value === 0 ? 'No cancellation fee applies' : `A cancellation fee of $${policy.policy.value} applies`}
                      </BodyText>
                      <BodyText variant="medium">
                        Accommodation bookings can be cancelled with a {policy.policy.value === 0 ? 'full' : 'partial'} refund provided the cancellation is made no less than {pluralizeToString('day', policy.policy.leadDaysMin)} prior to check-in.
                      </BodyText>
                    </>
                  )}
                </Group>
              </TermsGrid>
              <Divider kind="primary" />
            </>)}

          <TermsGrid>
            {nonRefundablePolicy && <BodyText variant="medium" weight="bold">
              {nonRefundablePolicy.policy.leadDaysMin === 0 ? 'Up to check-in' : `From ${nonRefundablePolicy.policy.leadDaysMin} days until check-in`}
            </BodyText>}
            <BodyText variant="medium">
              Non-refundable
            </BodyText>
          </TermsGrid>
        </Group>
        <HelpPane />
      </Group>
    </Modal>)
  }, [nonRefundablePolicy, orderedPolicies, refundable, refundDescriptions, showModal])

  return <>
    {refundable === Refundable.Partial || refundable === Refundable.Fully &&
    <TextLink
      size="medium"
      colour="highlight-secondary"
      underline={false}
      startIcon={<LineRefundIcon />}
      endIcon={<LineInfoCircleIcon colour="success" />}
      onClick={openPolicyModal}
    >
      {refundDescriptions[refundable]}
    </TextLink>}
    {refundable === Refundable.Non &&
      <TextLink
        size="medium"
        endIcon={<LineInfoCircleIcon />}
        onClick={openPolicyModal}
      >{refundDescriptions[refundable]}
      </TextLink>}
  </>
}

export default StaticConnectionCancellationPoliciesComponent
